import { formatCurrency } from "@app/deploy";
import cn from "classnames";
import type { ComponentProps } from "react";
import { IconInfo } from "./icons";
import { LoadingSpinner } from "./loading";
import { Tooltip, type TooltipProps } from "./tooltip";

export const COST_ESTIMATE_TOOLTIP_TEXT =
  "This is an estimate of the cost of running the current resources for one month. It is updated automatically as resources are added or scaled. Please note: it does not represent your actual usage for the month and does not reflect contract discounts, custom rates, or allocations.";

/**
 * Shared sizing + tooltip for a table's cost column. Spread into that
 * column's colDef: `{ ...costColDef, field: "cost", cellRenderer: ... }`.
 */
export const costColDef = {
  headerName: "Cost/mo",
  initialWidth: 125,
  maxWidth: 125,
  suppressSizeToFit: true,
  // Exempt from the grid's initial fit-to-content auto-size pass, which
  // otherwise measures only cell content and can render this narrower
  // than initialWidth before any resize happens.
  suppressAutoSize: true,
  headerTooltip: COST_ESTIMATE_TOOLTIP_TEXT,
};

/** Always-visible summary for contexts where tooltip copy was removed (e.g. scale UI). */
export const COST_ESTIMATE_VISIBLE_SUMMARY =
  "Updated automatically as resources are added or scaled.";

export interface CostEstimateInfoIconProps
  extends Omit<TooltipProps, "text" | "children" | "triggerButton"> {
  text?: string;
  /** Accessible name for the info control */
  ariaLabel?: string;
}

export const CostEstimateInfoIcon = ({
  text = COST_ESTIMATE_TOOLTIP_TEXT,
  ariaLabel = "More about estimated monthly cost",
  ...tooltipProps
}: CostEstimateInfoIconProps) => (
  <Tooltip
    text={text}
    triggerButton
    triggerAriaLabel={ariaLabel}
    {...tooltipProps}
  >
    <IconInfo
      className="inline-block opacity-50 hover:opacity-100 cursor-pointer"
      variant="sm"
      aria-hidden
    />
  </Tooltip>
);

export interface CostEstimateTooltipProps extends ComponentProps<"span"> {
  cost: number | null;
}

export const CostEstimateTooltip = ({
  cost,
  className,
  ...spanProps
}: CostEstimateTooltipProps) => (
  <span className={cn("flex items-center w-fit", className)} {...spanProps}>
    {cost == null ? <LoadingSpinner /> : <span>{formatCurrency(cost)}</span>}
  </span>
);
